import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../../axios/axiosInstance';
import apiEndpoints from '../../../../axios/api';

// Working Documents: the society's own papers - bye-laws, registration,
// minutes, audit reports, policies, forms ... Everyone with access sees the
// documents their role may see (Members / Committee / Admins); managers can
// upload, edit, replace with a new version and archive. Files are opened
// through the API (never a public link), so access is checked every time.

const CATEGORY_META = {
    BYLAWS: { label: 'Bye-laws', icon: 'mdi:gavel' },
    REGISTRATION: { label: 'Registration & Certificates', icon: 'mdi:certificate-outline' },
    AGM_MINUTES: { label: 'AGM / SGM Minutes', icon: 'mdi:account-group-outline' },
    COMMITTEE_MINUTES: { label: 'Committee Minutes', icon: 'mdi:clipboard-text-outline' },
    RESOLUTION: { label: 'Resolutions', icon: 'mdi:check-decagram-outline' },
    AUDIT_REPORT: { label: 'Audit Reports', icon: 'mdi:file-chart-outline' },
    POLICY: { label: 'Policies & Rules', icon: 'mdi:book-open-page-variant-outline' },
    FORM: { label: 'Forms & Formats', icon: 'mdi:form-select' },
    AGREEMENT: { label: 'Agreements & Contracts', icon: 'mdi:handshake-outline' },
    INSURANCE: { label: 'Insurance', icon: 'mdi:shield-check-outline' },
    NOC_PERMIT: { label: 'NOCs & Permits', icon: 'mdi:stamper' },
    CIRCULAR: { label: 'Circulars', icon: 'mdi:bullhorn-outline' },
    OTHER: { label: 'Other', icon: 'mdi:file-document-outline' },
};
const CATEGORIES = Object.keys(CATEGORY_META);
const VISIBILITY_META = {
    MEMBERS: { label: 'All members', cls: 'tw-bg-emerald-50 tw-text-emerald-700', icon: 'mdi:account-multiple-outline' },
    COMMITTEE: { label: 'Committee', cls: 'tw-bg-violet-50 tw-text-violet-700', icon: 'mdi:account-tie-outline' },
    ADMIN: { label: 'Admins only', cls: 'tw-bg-gray-100 tw-text-gray-700', icon: 'mdi:lock-outline' },
};
const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt';
const MAX_BYTES = 25 * 1024 * 1024;

const fmtDate = (v) => (v ? new Date(`${String(v).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const fmtSize = (b) => (!b ? '' : b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`);
const fileIcon = (mime, name) => {
    const n = String(name || '').toLowerCase();
    if ((mime || '').includes('pdf') || n.endsWith('.pdf')) return 'mdi:file-pdf-box';
    if ((mime || '').startsWith('image/')) return 'mdi:file-image-outline';
    if (/\.(xls|xlsx)$/.test(n)) return 'mdi:file-excel-outline';
    if (/\.(doc|docx)$/.test(n)) return 'mdi:file-word-outline';
    return 'mdi:file-outline';
};

const inputClass = 'tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-blue-500';
const labelClass = 'tw-block tw-text-[11px] tw-font-medium tw-text-gray-700 tw-mb-1';

// Open or download a document through the API.
const openDocument = async (doc, download = false) => {
    try {
        const access = await axiosInstance.get(apiEndpoints.SOCIETY_DOCUMENT_ACCESS_API.replace(':id', doc.document_id));
        const info = access?.data || {};
        if (info.url) {
            window.open(info.url, '_blank', 'noopener');
            return;
        }
        const blob = await axiosInstance.get(apiEndpoints.SOCIETY_DOCUMENT_FILE_API.replace(':id', doc.document_id), {
            params: download ? { download: '1' } : {},
            responseType: 'blob',
        });
        const file = blob instanceof Blob ? blob : new Blob([blob], { type: doc.mime_type || 'application/octet-stream' });
        const url = window.URL.createObjectURL(file);
        if (download) {
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.file_name || 'document';
            document.body.appendChild(a);
            a.click();
            a.remove();
        } else {
            window.open(url, '_blank', 'noopener');
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Could not open the document');
    }
};

const EMPTY = { title: '', category: 'BYLAWS', visibility: 'MEMBERS', document_date: '', valid_until: '', version_label: '', description: '', is_pinned: false };

// Upload a new document, edit details, or upload a new version.
const DocumentModal = ({ mode, doc, onClose, onSaved }) => {
    const [form, setForm] = useState(() => (doc ? {
        ...EMPTY,
        title: doc.title || '', category: doc.category, visibility: doc.visibility,
        document_date: mode === 'version' ? '' : (doc.document_date ? String(doc.document_date).slice(0, 10) : ''),
        valid_until: mode === 'version' ? '' : (doc.valid_until ? String(doc.valid_until).slice(0, 10) : ''),
        version_label: mode === 'version' ? '' : (doc.version_label || ''),
        description: doc.description || '', is_pinned: !!doc.is_pinned,
    } : EMPTY));
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);
    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
    const needsFile = mode !== 'edit';

    const pickFile = (f) => {
        if (!f) return;
        if (f.size > MAX_BYTES) { toast.warn('File must be under 25 MB'); return; }
        setFile(f);
        if (mode === 'create' && !form.title) set('title', f.name.replace(/\.[^.]+$/, ''));
    };

    const submit = async () => {
        if (!form.title.trim()) return toast.warn('Title is required');
        if (needsFile && !file) return toast.warn('Choose a file');
        if (form.document_date && form.valid_until && form.valid_until < form.document_date) return toast.warn('"Valid until" cannot be before the document date');
        setSaving(true);
        try {
            const fields = { ...form, is_pinned: form.is_pinned ? '1' : '0' };
            if (mode === 'edit') {
                await axiosInstance.put(apiEndpoints.SOCIETY_DOCUMENT_DETAIL_API.replace(':id', doc.document_id), { ...fields, is_pinned: form.is_pinned });
                toast.success('Document updated');
            } else {
                const fd = new FormData();
                fd.append('file', file);
                Object.entries(fields).forEach(([k, v]) => { if (v !== '' && v != null) fd.append(k, v); });
                const url = mode === 'version'
                    ? apiEndpoints.SOCIETY_DOCUMENT_VERSIONS_API.replace(':id', doc.document_id)
                    : apiEndpoints.SOCIETY_DOCUMENTS_API;
                await axiosInstance.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                toast.success(mode === 'version' ? 'New version uploaded' : 'Document uploaded');
            }
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Could not save');
        } finally {
            setSaving(false);
        }
    };

    const title = mode === 'edit' ? 'Edit Document Details' : mode === 'version' ? `Upload New Version — ${doc.title}` : 'Upload Document';
    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-p-4" onClick={onClose}>
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-xl tw-max-h-[92vh] tw-flex tw-flex-col" onClick={e => e.stopPropagation()}>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <h2 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-truncate">{title}</h2>
                    <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500"><Icon icon="mdi:close" className="tw-text-lg" /></button>
                </div>
                <div className="tw-p-5 tw-space-y-3 tw-overflow-y-auto">
                    {needsFile && (
                        <div
                            onClick={() => fileRef.current?.click()}
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
                            className="tw-border-2 tw-border-dashed tw-border-gray-200 hover:tw-border-blue-400 tw-rounded-lg tw-p-4 tw-text-center tw-cursor-pointer"
                        >
                            <input ref={fileRef} type="file" accept={ACCEPT} className="tw-hidden" onChange={e => pickFile(e.target.files?.[0])} />
                            {file ? (
                                <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-text-gray-700">
                                    <Icon icon={fileIcon(file.type, file.name)} className="tw-text-2xl tw-text-blue-600" />
                                    <span className="tw-truncate">{file.name}</span>
                                    <span className="tw-text-xs tw-text-gray-400">{fmtSize(file.size)}</span>
                                </div>
                            ) : (
                                <>
                                    <Icon icon="mdi:cloud-upload-outline" className="tw-text-3xl tw-text-gray-300 tw-mx-auto" />
                                    <p className="tw-text-xs tw-text-gray-600 tw-mt-1">Click or drop a file here</p>
                                    <p className="tw-text-[10px] tw-text-gray-400">PDF, Word, Excel, image or text · up to 25 MB</p>
                                </>
                            )}
                        </div>
                    )}
                    {mode !== 'version' && (
                        <div>
                            <label className={labelClass}>Title <span className="tw-text-red-500">*</span></label>
                            <input value={form.title} maxLength={200} onChange={e => set('title', e.target.value)} className={inputClass} placeholder="e.g. Model Bye-laws (2024 amendment)" />
                        </div>
                    )}
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                        {mode !== 'version' && (
                            <>
                                <div>
                                    <label className={labelClass}>Category</label>
                                    <select value={form.category} onChange={e => set('category', e.target.value)} className={inputClass}>
                                        {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_META[c].label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Who can see it</label>
                                    <select value={form.visibility} onChange={e => set('visibility', e.target.value)} className={inputClass}>
                                        <option value="MEMBERS">All members (residents too)</option>
                                        <option value="COMMITTEE">Committee and admins</option>
                                        <option value="ADMIN">Admins / managers only</option>
                                    </select>
                                </div>
                            </>
                        )}
                        <div>
                            <label className={labelClass}>Document date</label>
                            <input type="date" value={form.document_date} onChange={e => set('document_date', e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>Valid until <span className="tw-text-gray-400 tw-font-normal">(certificates, insurance)</span></label>
                            <input type="date" value={form.valid_until} onChange={e => set('valid_until', e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>Version / reference</label>
                            <input value={form.version_label} maxLength={40} onChange={e => set('version_label', e.target.value)} className={inputClass} placeholder={mode === 'version' ? 'e.g. Amended 2026' : 'e.g. v1, AGM 2025'} />
                        </div>
                        {mode !== 'version' && (
                            <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-700 tw-mt-5 tw-cursor-pointer">
                                <input type="checkbox" checked={form.is_pinned} onChange={e => set('is_pinned', e.target.checked)} />
                                Pin to top
                            </label>
                        )}
                    </div>
                    {mode !== 'version' && (
                        <div>
                            <label className={labelClass}>Description</label>
                            <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)} className={inputClass} />
                        </div>
                    )}
                    {mode === 'version' && (
                        <p className="tw-text-[11px] tw-text-gray-500">The current file is kept in the document's history; members will see the new one.</p>
                    )}
                </div>
                <div className="tw-flex tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100">
                    <button onClick={onClose} className="tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 hover:tw-bg-gray-50">Cancel</button>
                    <button onClick={submit} disabled={saving} className="tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 disabled:tw-opacity-50">
                        {saving ? 'Saving…' : mode === 'edit' ? 'Save' : 'Upload'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const HistoryModal = ({ doc, onClose }) => {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        axiosInstance.get(apiEndpoints.SOCIETY_DOCUMENT_HISTORY_API.replace(':id', doc.document_id))
            .then(res => setRows(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setRows([]));
    }, [doc]);
    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-p-4" onClick={onClose}>
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-lg" onClick={e => e.stopPropagation()}>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <h2 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-truncate">Versions — {doc.title}</h2>
                    <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500"><Icon icon="mdi:close" className="tw-text-lg" /></button>
                </div>
                <ul className="tw-p-3 tw-max-h-[60vh] tw-overflow-y-auto">
                    {!rows && <li className="tw-text-center tw-py-6 tw-text-xs tw-text-gray-400">Loading…</li>}
                    {rows?.map(r => (
                        <li key={r.document_id} className="tw-flex tw-items-center tw-gap-3 tw-px-2 tw-py-2 tw-rounded hover:tw-bg-gray-50">
                            <Icon icon={fileIcon(r.mime_type, r.file_name)} className="tw-text-xl tw-text-gray-500" />
                            <div className="tw-flex-1 tw-min-w-0">
                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-truncate">
                                    {r.version_label || r.file_name}
                                    {r.is_current ? <span className="tw-ml-1.5 tw-text-[10px] tw-px-1.5 tw-rounded tw-bg-emerald-50 tw-text-emerald-700">Current</span> : null}
                                </p>
                                <p className="tw-text-[10px] tw-text-gray-400">{fmtDate(r.document_date) || fmtDate(r.created_at)} · {r.uploaded_by_name || '—'} · {fmtSize(r.size_bytes)}</p>
                            </div>
                            <button onClick={() => openDocument(r)} className="tw-p-1 tw-rounded hover:tw-bg-blue-50 tw-text-blue-600" title="Open"><Icon icon="mdi:eye-outline" /></button>
                            <button onClick={() => openDocument(r, true)} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500" title="Download"><Icon icon="mdi:download" /></button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

const WorkingDocuments = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [category, setCategory] = useState('');
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [modal, setModal] = useState(null); // { mode, doc }
    const [historyDoc, setHistoryDoc] = useState(null);
    const [archiveDoc, setArchiveDoc] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(apiEndpoints.SOCIETY_DOCUMENTS_API, { params: { category: category || undefined, search: debounced || undefined } });
            setData(res?.data || null);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to load documents');
        } finally {
            setLoading(false);
        }
    }, [category, debounced]);

    useEffect(() => { load(); }, [load]);
    usePageRefresh(load);

    const docs = useMemo(() => data?.documents || [], [data]);
    const canManage = !!data?.can_manage;
    const grouped = useMemo(() => {
        const map = new Map();
        docs.forEach(d => {
            const key = d.is_pinned ? '__PINNED' : d.category;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(d);
        });
        const order = ['__PINNED', ...CATEGORIES];
        return order.filter(k => map.has(k)).map(k => ({ key: k, docs: map.get(k) }));
    }, [docs]);

    const confirmArchive = async () => {
        try {
            await axiosInstance.delete(apiEndpoints.SOCIETY_DOCUMENT_DETAIL_API.replace(':id', archiveDoc.document_id));
            toast.success('Document archived');
            setArchiveDoc(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Could not archive');
        }
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-4">
                <div>
                    <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Working Documents</h1>
                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Bye-laws, certificates, minutes, policies and other society documents</p>
                </div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <div className="tw-relative">
                        <Icon icon="mdi:magnify" className="tw-absolute tw-left-2 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400" />
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search documents" className="tw-pl-7 tw-pr-2 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md tw-w-48 focus:tw-outline-none focus:tw-border-blue-500" />
                    </div>
                    <select value={category} onChange={e => setCategory(e.target.value)} className="tw-text-xs tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-bg-white">
                        <option value="">All categories</option>
                        {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_META[c].label}</option>)}
                    </select>
                    {canManage && (
                        <button onClick={() => setModal({ mode: 'create' })} className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700">
                            <Icon icon="mdi:upload" /> Upload Document
                        </button>
                    )}
                </div>
            </div>

            {loading && !data ? (
                <div className="tw-flex tw-justify-center tw-py-16"><div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600" /></div>
            ) : docs.length === 0 ? (
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-text-center tw-py-16">
                    <Icon icon="mdi:folder-open-outline" className="tw-text-5xl tw-text-gray-200 tw-mx-auto" />
                    <p className="tw-text-sm tw-text-gray-500 tw-mt-2">{debounced || category ? 'No documents match.' : 'No documents yet.'}</p>
                    {canManage && !debounced && !category && <p className="tw-text-xs tw-text-gray-400 tw-mt-1">Start with the society's bye-laws and registration certificate.</p>}
                </div>
            ) : (
                <div className="tw-space-y-4">
                    {grouped.map(g => {
                        const meta = g.key === '__PINNED' ? { label: 'Pinned', icon: 'mdi:pin-outline' } : CATEGORY_META[g.key];
                        return (
                            <div key={g.key} className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                                <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-border-b tw-border-gray-100">
                                    <Icon icon={meta.icon} className="tw-text-base tw-text-blue-600" />
                                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800">{meta.label}</h2>
                                    <span className="tw-text-[11px] tw-text-gray-400">({g.docs.length})</span>
                                </div>
                                <ul className="tw-divide-y tw-divide-gray-50">
                                    {g.docs.map(d => {
                                        const vis = VISIBILITY_META[d.visibility] || VISIBILITY_META.MEMBERS;
                                        return (
                                            <li key={d.document_id} className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-px-4 tw-py-2.5 hover:tw-bg-gray-50/60">
                                                <Icon icon={fileIcon(d.mime_type, d.file_name)} className="tw-text-2xl tw-text-gray-500 tw-flex-shrink-0" />
                                                <div className="tw-flex-1 tw-min-w-[220px]">
                                                    <button onClick={() => openDocument(d)} className="tw-text-sm tw-font-medium tw-text-gray-800 hover:tw-text-blue-600 tw-text-left">{d.title}</button>
                                                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-0.5 tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                                        {g.key === '__PINNED' && <span>{CATEGORY_META[d.category]?.label}</span>}
                                                        {d.version_label && <span className="tw-font-medium tw-text-gray-600">{d.version_label}</span>}
                                                        {d.document_date && <span>Dated {fmtDate(d.document_date)}</span>}
                                                        {d.valid_until && (
                                                            <span className={d.is_expired ? 'tw-text-rose-600 tw-font-medium' : ''}>
                                                                {d.is_expired ? 'Expired' : 'Valid until'} {fmtDate(d.valid_until)}
                                                            </span>
                                                        )}
                                                        <span>{fmtSize(d.size_bytes)}</span>
                                                        {d.previous_versions > 0 && (
                                                            <button onClick={() => setHistoryDoc(d)} className="tw-text-blue-600 hover:tw-underline">{d.previous_versions} earlier version{d.previous_versions > 1 ? 's' : ''}</button>
                                                        )}
                                                    </div>
                                                    {d.description && <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5 tw-line-clamp-2">{d.description}</p>}
                                                </div>
                                                {canManage && (
                                                    <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium ${vis.cls}`} title="Who can see this document">
                                                        <Icon icon={vis.icon} className="tw-text-xs" />{vis.label}
                                                    </span>
                                                )}
                                                <div className="tw-flex tw-items-center tw-gap-0.5">
                                                    <button onClick={() => openDocument(d)} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-blue-600" title="Open"><Icon icon="mdi:eye-outline" /></button>
                                                    <button onClick={() => openDocument(d, true)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500" title="Download"><Icon icon="mdi:download" /></button>
                                                    {canManage && (
                                                        <>
                                                            <button onClick={() => setModal({ mode: 'version', doc: d })} className="tw-p-1.5 tw-rounded hover:tw-bg-indigo-50 tw-text-indigo-600" title="Upload new version"><Icon icon="mdi:file-replace-outline" /></button>
                                                            <button onClick={() => setModal({ mode: 'edit', doc: d })} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500" title="Edit details"><Icon icon="mdi:pencil-outline" /></button>
                                                            <button onClick={() => setArchiveDoc(d)} className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-500" title="Archive"><Icon icon="mdi:archive-arrow-down-outline" /></button>
                                                        </>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            )}

            {modal && <DocumentModal mode={modal.mode} doc={modal.doc} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
            {historyDoc && <HistoryModal doc={historyDoc} onClose={() => setHistoryDoc(null)} />}
            {archiveDoc && (
                <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-p-4" onClick={() => setArchiveDoc(null)}>
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-sm tw-p-5" onClick={e => e.stopPropagation()}>
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Archive "{archiveDoc.title}"?</h3>
                        <p className="tw-text-xs tw-text-gray-600 tw-mt-2">It and its earlier versions will no longer be shown to anyone.</p>
                        <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
                            <button onClick={() => setArchiveDoc(null)} className="tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200">Cancel</button>
                            <button onClick={confirmArchive} className="tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700">Archive</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkingDocuments;
